// @flow
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import './CartChangeNotice.css';
// Components
import {
  ProgressiveImageLoader,
  priceConverter,
  quantityConverter,
} from '../../common';
import AddToCartButton from '../containers/AddToCartButton';
// Types
import type { Blend } from '../../blend/types/Blend';
import type { SimpleLineItem } from '../types';

// How many products the "added to cart" notice lists before summarizing the rest
const MAX_ADDED_ITEMS = 3;

type Props = {
  cartChanges: SimpleLineItem[],
  blends: Blend[],
  onDismiss: () => void,
  // 'added' right after a cart change, 'cart' when previewing the whole cart
  variant?: 'added' | 'cart',
  // 'bottom' drops down from the header cart, 'sheet' fills the expanded mobile bottom sheet
  placement?: 'bottom' | 'sheet',
  itemCount?: number,
  subtotal?: number,
  // Enables the remove button on each product
  onRemoveItem?: (lineItemId: number) => Promise<void>,
  countdown?: React$Node,
};

type NoticeRow = {
  key: string,
  name: string,
  link: ?string,
  image: any,
  quantity: string,
  total: ?string,
  isFreebie: boolean,
  // Product whose quantity can be changed from the notice, if any
  product: ?{
    id: number,
    lineItemId: number,
    unitForQuantity: string,
    incrementQuantity: number,
    defaultQuantity: number,
  },
};

export const pluralizeProducts = (count: number) =>
  `${count} ${count === 1 ? 'produto' : 'produtos'}`;

const CartChangeNotice = ({
  cartChanges,
  blends,
  onDismiss,
  variant = 'added',
  placement = 'bottom',
  itemCount,
  subtotal,
  onRemoveItem,
  countdown,
}: Props) => {
  // Line items being removed, to show their loading state
  const [removingIds, setRemovingIds] = useState([]);

  const rows: NoticeRow[] = useMemo(() => {
    const lineItemRows = [];
    const blendIds = new Set();

    cartChanges.forEach((item, index) => {
      if (item.mixId) {
        blendIds.add(item.mixId);
        return;
      }

      lineItemRows.push({
        key: `item-${item.id ?? index}`,
        name: item.name,
        link: item.isFreebie ? null : `/produtos/${item.slug}/`,
        image: item.images?.[0] ?? null,
        quantity: quantityConverter(item.quantity, item.unitForQuantity),
        total: item.total,
        isFreebie: !!item.isFreebie,
        product: item.canChangeQuantity
          ? {
              id: item.variantId,
              lineItemId: item.id,
              unitForQuantity: item.unitForQuantity,
              incrementQuantity: item.incrementQuantity,
              defaultQuantity: item.defaultQuantity,
            }
          : null,
      });
    });

    const blendRows = blends
      .filter(blend => blendIds.has(blend.id))
      .map(blend => ({
        key: `blend-${blend.id}`,
        name: blend.name,
        link: '/blend/meus-blends/',
        image: blend.lineItems?.[0]?.variant?.images?.[0] ?? null,
        quantity: quantityConverter(blend.quantity, 'g'),
        total: blend.total,
        isFreebie: false,
        product: null,
      }));

    return [...lineItemRows, ...blendRows];
  }, [cartChanges, blends]);

  const isAddedVariant = variant === 'added';
  const visibleRows = isAddedVariant ? rows.slice(0, MAX_ADDED_ITEMS) : rows;
  const hiddenRowsCount = rows.length - visibleRows.length;

  const handleRemoveItem = async (lineItemId: number) => {
    setRemovingIds(ids => [...ids, lineItemId]);
    await onRemoveItem(lineItemId);
    setRemovingIds(ids => ids.filter(id => id !== lineItemId));
  };

  const linkToProduct = (row: NoticeRow, content: React$Node) =>
    row.link ? (
      <Link to={row.link} onClick={onDismiss}>
        {content}
      </Link>
    ) : (
      content
    );

  if (rows.length === 0) return null;

  return (
    <div
      className={`cart-change-notice cart-change-notice--${placement}`}
      role="dialog"
      aria-label={isAddedVariant ? 'Carrinho atualizado' : 'Meu carrinho'}
    >
      <div className="cart-change-notice-header">
        {isAddedVariant ? (
          <span className="cart-change-notice-title cart-change-notice-title--success">
            <i className="fa fa-check-circle" aria-hidden="true" />
            Carrinho atualizado
          </span>
        ) : (
          <span className="cart-change-notice-title">
            Meu carrinho
            {itemCount != null && (
              <span className="cart-change-notice-count">
                {pluralizeProducts(itemCount)}
              </span>
            )}
          </span>
        )}

        <button
          type="button"
          className="cart-change-notice-close"
          onClick={onDismiss}
          aria-label="Fechar"
        >
          <i className="fa fa-times" aria-hidden="true" />
        </button>
      </div>

      <ul
        className={`cart-change-notice-list ${
          isAddedVariant ? '' : 'cart-change-notice-list--scrollable'
        }`}
      >
        {visibleRows.map(row => (
          <li key={row.key} className="cart-change-notice-item">
            <div className="cart-change-notice-image">
              {linkToProduct(
                row,
                <ProgressiveImageLoader
                  selectedImage={row.image}
                  imageToShow={'small'}
                />,
              )}
            </div>

            <div className="cart-change-notice-info">
              <p className="cart-change-notice-name" title={row.name}>
                {linkToProduct(row, row.name)}
              </p>
              {row.product ? (
                <div className="cart-change-notice-quantity-control">
                  <AddToCartButton
                    productId={row.product.id}
                    unitForQuantity={row.product.unitForQuantity}
                    defaultQuantity={row.product.defaultQuantity}
                    incrementQuantity={row.product.incrementQuantity}
                    inStock={true}
                    // Removing is done by the remove button, as in the cart page
                    disableInsteadOfDeleteOnMinimumQuantity={!!onRemoveItem}
                    analyticsViewSource={'cart preview'} // USE ANALYTICS
                  />
                  {onRemoveItem && (
                    <button
                      type="button"
                      className="cart-change-notice-remove"
                      onClick={() => handleRemoveItem(row.product.lineItemId)}
                      disabled={removingIds.includes(row.product.lineItemId)}
                      title="Remover do carrinho"
                      aria-label="Remover do carrinho"
                    >
                      <i
                        className={
                          removingIds.includes(row.product.lineItemId)
                            ? 'fa fa-spinner fa-pulse'
                            : 'fa fa-trash-o'
                        }
                        aria-hidden="true"
                      />
                    </button>
                  )}
                </div>
              ) : (
                <span className="cart-change-notice-quantity">
                  {row.quantity}
                </span>
              )}
            </div>

            {row.isFreebie ? (
              <span className="cart-change-notice-freebie">Brinde</span>
            ) : (
              row.total != null && (
                <span className="cart-change-notice-price">
                  {priceConverter(row.total)}
                </span>
              )
            )}
          </li>
        ))}
      </ul>

      {hiddenRowsCount > 0 && (
        <p className="cart-change-notice-more">
          e mais {pluralizeProducts(hiddenRowsCount)}
        </p>
      )}

      <div className="cart-change-notice-footer">
        {subtotal != null && (
          <div className="cart-change-notice-subtotal">
            <span>Subtotal</span>
            <strong>{priceConverter(subtotal)}</strong>
          </div>
        )}

        <Link className="cart-change-notice-button" to="/carrinho/">
          Ver carrinho
        </Link>
      </div>

      {countdown && (
        <div className="cart-change-notice-countdown">{countdown}</div>
      )}
    </div>
  );
};

export default CartChangeNotice;
